import { useContext } from 'react';
import { Sparkles, Lock, Zap, User, Heart, Shield, Star, Users } from 'lucide-react';
import { AuthContext } from '../App';

const PERSONAS = [
  { category: 'Male Energy', items: [
    { name: 'Dad', icon: '👔', description: 'The steady rock. Terrible puns, perfect advice.' },
    { name: 'Big Bro', icon: '🤝', description: 'Been through it. Keeps it real.' },
    { name: 'Grandpa', icon: '🪑', description: 'Seen it all. Patient wisdom.' },
    { name: 'The Real One', icon: '🔥', description: 'Street smart. Real talk. Loyalty.' },
    { name: 'Uncle', icon: '🎱', description: 'The cool one. No judgment.' },
  ]},
  { category: 'Female Energy', items: [
    { name: 'Mom', icon: '🌸', description: 'Warm. Fierce. Protective.' },
    { name: 'Big Sis', icon: '💅', description: 'Honest. Has your back.' },
    { name: 'Grandma', icon: '🧶', description: 'Unconditional love. Gentle wisdom.' },
    { name: 'Auntie', icon: '💎', description: 'The real one. Tells it straight.' },
    { name: 'Best Friend', icon: '💕', description: 'Gets it. No filter. Ride or die.' },
  ]},
  { category: 'Neutral / Spiritual', items: [
    { name: 'Mentor', icon: '🧭', description: 'Wise. Strategic. Experienced.' },
    { name: 'Listener', icon: '🌙', description: 'Pure presence. Holds space.' },
    { name: 'Elder', icon: '🌿', description: 'Community wisdom. Life experience.' },
    { name: 'Spiritual Guide', icon: '🕊', description: 'Faith-based comfort. Bigger picture.' },
    { name: 'Coach', icon: '🏆', description: 'Fired up. Believes in you.' },
  ]},
];

const ADAPTIVE_OPTION = { name: 'Adaptive', description: 'AI reads the room and picks the right person for you' };

const RESPONSE_STYLES = [
  ...PERSONAS.flatMap(c => c.items),
  ADAPTIVE_OPTION,
];

const FREE_PERSONAS = ['Dad', 'Mom', 'Big Bro', 'Big Sis'];

const StyleSelector = ({ selectedStyle, onSelect }) => {
  const { user } = useContext(AuthContext);
  const isPremium = user?.is_premium;

  return (
    <div>
      <label className="text-sm uppercase tracking-[0.2em] font-bold text-[#A1A1AA] mb-5 block">
        <Sparkles className="w-4 h-4 inline mr-2" />
        Choose Your Person
      </label>

      {PERSONAS.map((category) => (
        <div key={category.category} className="mb-5">
          <p className="text-sm uppercase tracking-widest text-[#71717A] mb-3 font-semibold">{category.category}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {category.items.map((persona) => {
              const isFree = FREE_PERSONAS.includes(persona.name);
              const locked = !isFree && !isPremium;
              return (
                <button
                  key={persona.name}
                  type="button"
                  onClick={() => !locked && onSelect(persona.name)}
                  className={`style-pill text-left p-3 ${selectedStyle === persona.name ? 'active' : ''} ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`persona-${persona.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg">{persona.icon}</span>
                    {locked && <Lock className="w-3 h-3 text-[#71717A]" />}
                  </div>
                  <div className="font-semibold text-sm mt-1">{persona.name}</div>
                  <div className="text-xs text-[#B4B4BD] mt-0.5 leading-tight">{persona.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-2">
        <p className="text-sm uppercase tracking-widest text-[#71717A] mb-3 font-semibold">Premium</p>
        <button
          type="button"
          onClick={() => isPremium && onSelect('Adaptive')}
          className={`style-pill text-left p-3 w-full ${selectedStyle === 'Adaptive' ? 'active' : ''} ${!isPremium ? 'opacity-60 cursor-not-allowed' : ''}`}
          data-testid="persona-adaptive"
        >
          <div className="flex items-center gap-2">
            {isPremium ? <Zap className="w-5 h-5 text-[#D4AF37]" /> : <Lock className="w-5 h-5 text-[#71717A]" />}
            <div>
              <div className="font-semibold text-sm">Adaptive</div>
              <div className="text-xs text-[#A1A1AA]">
                {isPremium ? 'AI reads the room and picks the right person for you' : 'Premium — AI reads the room for you'}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export { StyleSelector, RESPONSE_STYLES };
